import { Box, Button, Theme, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useFormContext } from "react-hook-form";
import { FieldProps } from "@/core/components/Field";
import useCustomStyles from "@/core/hooks/useCustomStyles/useCustomStyles";
import { useDefaultValue } from "@/core/hooks/useDefaultValue/useDefaultValue";
import { parseDefaultValue } from "@/core/utils/parseDefaultValue";

const styles = (theme: Theme) => ({
  numberWrapper: {
    width: "80px",
    height: "48px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    border: `1px solid ${theme.palette.grey.A400}`,
    borderRadius: "4px",
    backgroundColor: theme.palette.background.default,
  },
  buttons: {
    minWidth: "48px",
    height: "48px",
    borderColor: theme.palette.grey[800],
    backgroundColor: theme.palette.background.default,
  },
  iconsWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexWrap: "wrap",
    maxWidth: "290px",
    margin: "0 auto",
    marginTop: theme.spacing(3),
  },
});

const MAX_NUMBER = 20;
const MIN_NUMBER = 1;
const DEFAULT_NUMBER = 2;

export const SelectNumber = ({ field }: FieldProps) => {
  const [number, setNumber] = useState(DEFAULT_NUMBER);
  const classes: any = useCustomStyles(styles);
  const { setValue } = useFormContext();

  const defaultValue = parseDefaultValue(field.defaultValue);
  const defaultNumberOfPassengers =
    useDefaultValue(defaultValue?.variableName) ?? defaultValue?.value;

  useEffect(() => {
    if (defaultNumberOfPassengers) {
      setNumber(+defaultNumberOfPassengers);
    }
  }, [defaultNumberOfPassengers]);

  useEffect(() => {
    setValue(field.key, number);
  }, [number]);

  const incrementNumber = () => {
    const maxNumber = field.validate?.max ?? MAX_NUMBER;
    if (number < maxNumber) {
      setNumber(number + 1);
    }
  };

  const decrementNumber = () => {
    const minNumber = field.validate?.min ?? MIN_NUMBER;
    if (number > minNumber) {
      setNumber(number - 1);
    }
  };

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap={1}
        p={0}
      >
        <Button
          data-cy="decrement"
          sx={classes?.buttons}
          variant="outlined"
          size="small"
          onClick={decrementNumber}
        >
          <RemoveIcon color="secondary" />
        </Button>
        <Box sx={classes?.numberWrapper}>
          <Typography variant="h2">{number}</Typography>
        </Box>
        <Button
          data-cy="increment"
          sx={classes?.buttons}
          variant="outlined"
          size="small"
          onClick={incrementNumber}
        >
          <AddIcon color="secondary" />
        </Button>
      </Box>
    </>
  );
};
